import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { TeamEventListItem } from '../../services/api/teamEvents';

interface TeamEventCalendarProps {
  events: TeamEventListItem[];
  isAdmin?: boolean;
}

interface CalendarDay {
  date: string;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  gridIndex: number;
}

interface EventSpan {
  id: string;
  eventId: number;
  label: string;
  typeName: string;
  startIndex: number;
  endIndex: number;
  track: number;
  color: string;
  isCancelled: boolean;
}

const EVENT_TYPE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  default: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800' },
};

const PALETTE = [
  { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800' },
  { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800' },
  { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800' },
  { bg: 'bg-amber-100', border: 'border-amber-300', text: 'text-amber-800' },
  { bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-800' },
  { bg: 'bg-cyan-100', border: 'border-cyan-300', text: 'text-cyan-800' },
];

const CANCELLED_COLOR = { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-400' };

function formatDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getMonthName(month: number): string {
  return ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'][month] || '';
}

function assignTracks(spans: EventSpan[]) {
  for (const span of spans) {
    const weekRow = Math.floor(span.startIndex / 7);
    const sameWeek = spans.filter(
      (s) => s !== span && s.track >= 0 && Math.floor(s.startIndex / 7) === weekRow
    );
    let track = 0;
    while (sameWeek.some((s) => s.track === track && s.startIndex <= span.endIndex && s.endIndex >= span.startIndex)) {
      track++;
    }
    span.track = track;
  }
}

export default function TeamEventCalendar({ events, isAdmin = false }: TeamEventCalendarProps) {
  const navigate = useNavigate();
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  const handlePrevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
  };
  const handleNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
  };
  const handleToday = () => { setCurrentMonth(today.getMonth()); setCurrentYear(today.getFullYear()); };

  const calendarDays = useMemo((): CalendarDay[] => {
    const days: CalendarDay[] = [];
    let gridIndex = 0;
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;
    const prevLast = new Date(currentYear, currentMonth, 0);
    for (let i = startDow - 1; i >= 0; i--) {
      const d = new Date(prevLast);
      d.setDate(prevLast.getDate() - i);
      days.push({ date: formatDate(d), dayOfMonth: d.getDate(), isCurrentMonth: false, gridIndex: gridIndex++ });
    }
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push({ date: formatDate(new Date(currentYear, currentMonth, day)), dayOfMonth: day, isCurrentMonth: true, gridIndex: gridIndex++ });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(currentYear, currentMonth + 1, i);
      days.push({ date: formatDate(d), dayOfMonth: i, isCurrentMonth: false, gridIndex: gridIndex++ });
    }
    return days;
  }, [currentMonth, currentYear]);

  // Build color map per event type
  const typeColorMap = useMemo(() => {
    const map = new Map<string, typeof PALETTE[0]>();
    const uniqueTypes = [...new Set(events.map((e) => e.eventType.name))];
    uniqueTypes.forEach((t, i) => map.set(t, PALETTE[i % PALETTE.length]!));
    return map;
  }, [events]);

  const eventSpans = useMemo((): EventSpan[] => {
    const spans: EventSpan[] = [];
    const visibleEvents = events.filter(
      (e) => e.status === 'published' || (isAdmin && e.status === 'cancelled'),
    );

    for (const event of visibleEvents) {
      const startDate = event.eventDate;
      const endDate = event.endDate || event.eventDate;
      const label = event.title || event.eventType.name;
      const isCancelled = event.status === 'cancelled';
      const colors = isCancelled
        ? CANCELLED_COLOR
        : (typeColorMap.get(event.eventType.name) ?? EVENT_TYPE_COLORS.default!);

      const startIdx = calendarDays.findIndex((d) => d.date === startDate);
      const endIdx = calendarDays.findIndex((d) => d.date === endDate);

      // Skip events entirely outside the visible grid
      if (startIdx === -1 && endIdx === -1) continue;

      // For single-day events (no endDate or same as start), skip if not in grid
      if (startDate === endDate && startIdx === -1) continue;

      const effectiveStart = Math.max(startIdx === -1 ? 0 : startIdx, 0);
      const effectiveEnd = Math.min(endIdx === -1 ? calendarDays.length - 1 : endIdx, calendarDays.length - 1);

      // Safety: skip if range is inverted (endDate before startDate in grid)
      if (effectiveStart > effectiveEnd) continue;

      let cur = effectiveStart;
      while (cur <= effectiveEnd) {
        const weekEnd = Math.floor(cur / 7) * 7 + 6;
        const spanEnd = Math.min(effectiveEnd, weekEnd);
        spans.push({
          id: `${event.id}-${cur}-${spanEnd}`,
          eventId: event.id,
          label,
          typeName: event.eventType.name,
          startIndex: cur,
          endIndex: spanEnd,
          track: 0,
          color: `${colors.bg} ${colors.border} ${colors.text}`,
          isCancelled,
        });
        cur = spanEnd + 1;
      }
    }

    assignTracks(spans);
    return spans;
  }, [events, calendarDays, typeColorMap, isAdmin]);

  const rowHeights = useMemo((): Map<number, number> => {
    const heights = new Map<number, number>();
    for (const span of eventSpans) {
      const week = Math.floor(span.startIndex / 7);
      // Bars render at topInCell = 38 + track * 20 with height 18.
      // Bottom of bar on track N is 38 + N * 20 + 18 = 56 + N * 20.
      // Add 4px bottom pad so the bar does not touch the cell border.
      const needed = 38 + (span.track + 1) * 20 + 4;
      heights.set(week, Math.max(heights.get(week) ?? 0, needed, 80));
    }
    for (let w = 0; w < 6; w++) if (!heights.has(w)) heights.set(w, 80);
    return heights;
  }, [eventSpans]);

  const rowTops = useMemo((): Map<number, number> => {
    // The grid has a weekday header row (32px) followed by 6 week rows.
    // Rows are separated by a 4px gap (grid `gap-1`).
    // rowTops.get(w) returns the Y offset (from the top of the .relative container)
    // of the TOP edge of week row w, accounting for the variable height of preceding rows.
    const headerHeight = 32; // weekday header row
    const gapPx = 4;
    const tops = new Map<number, number>();
    let y = headerHeight + gapPx; // first week row starts below header + one gap
    for (let w = 0; w < 6; w++) {
      tops.set(w, y);
      const h = rowHeights.get(w) ?? 80;
      y += h + gapPx;
    }
    return tops;
  }, [rowHeights]);

  const todayStr = formatDate(today);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {getMonthName(currentMonth)} {currentYear}
        </h2>
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Previous month" onClick={handlePrevMonth} className="p-1 rounded hover:bg-gray-100"><ChevronLeftIcon className="w-5 h-5 text-gray-600" /></button>
          <button type="button" onClick={handleToday} className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded">Today</button>
          <button type="button" aria-label="Next month" onClick={handleNextMonth} className="p-1 rounded hover:bg-gray-100"><ChevronRightIcon className="w-5 h-5 text-gray-600" /></button>
        </div>
      </div>

      <div className="relative">
        <div className="grid grid-cols-7 gap-1">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
            <div key={d} className="text-center text-xs font-medium text-gray-500 py-2">{d}</div>
          ))}

          {calendarDays.map((day) => {
            const isToday = day.date === todayStr;
            const week = Math.floor(day.gridIndex / 7);
            const h = rowHeights.get(week) ?? 80;
            return (
              <div
                key={day.gridIndex}
                className={`border border-gray-100 rounded p-1 relative ${day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'}`}
                style={{ minHeight: `${h}px` }}
              >
                <div className={`relative z-20 text-sm font-medium ${isToday ? 'bg-primary-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}>
                  {day.dayOfMonth}
                </div>
              </div>
            );
          })}
        </div>

        {/* Event spans overlay */}
        {eventSpans.map((span) => {
          const startCol = span.startIndex % 7;
          const endCol = span.endIndex % 7;
          const weekRow = Math.floor(span.startIndex / 7);
          const rowTop = rowTops.get(weekRow) ?? 0;

          // rowTop = cumulative Y of this week's top edge (handles variable row heights).
          // +38px = in-cell offset for the date number.
          // track * 20px = stacked-event vertical stride.
          const top = `${rowTop + 38 + span.track * 20}px`;
          const left = `calc(${(startCol / 7) * 100}% + 2px)`;
          const width = `calc(${((endCol - startCol + 1) / 7) * 100}% - 4px)`;

          return (
            <button
              key={span.id}
              onClick={() => navigate(`/hr/team-events/${span.eventId}`)}
              className={`absolute truncate text-xs font-semibold px-1.5 py-0.5 rounded border cursor-pointer hover:opacity-80 transition-opacity ${
                span.isCancelled
                  ? `${CANCELLED_COLOR.bg} ${CANCELLED_COLOR.border} ${CANCELLED_COLOR.text} line-through`
                  : span.color
              }`}
              style={{ top, left, width, height: '18px', zIndex: 10 }}
              title={span.label}
            >
              {span.label}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-gray-100">
        {[...typeColorMap.entries()].map(([name, colors]) => (
          <div key={name} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className={`w-3 h-3 rounded ${colors.bg} border ${colors.border}`} />
            {name}
          </div>
        ))}
      </div>
    </div>
  );
}
